import axios, { AxiosRequestConfig } from "axios";
import { getAccessToken, getRefreshToken, updateAccessToken } from "src/hooks/useAuth";
import { HOST_CORE_API, HOST_PAYMENT_API } from "./config";
import { Storage } from "src/utils/storage";
import { PATH_LOGOUT, PATH_MAINTENANCE } from "src/routes/paths";

const company = Storage.getItem("currentCompany");
const companyId = company?.id;

export const axiosPaymentInstance = axios.create({
    baseURL: HOST_PAYMENT_API,
});

// https://core.chargewisedev.eu/api/doc/common
// https://core.chargewisedev.eu/api/doc
export const axiosCoreInstance = axios.create({
    baseURL: HOST_CORE_API,
});

// https://core.chargewisedev.eu/api/doc/common
// https://core.chargewisedev.eu/api/doc
export const axiosAuthInstance = axios.create({
    baseURL: HOST_CORE_API,
});

// https://core.chargewisedev.eu/api/doc (only company related endpoints)
export const axiosCompanyInstance = axios.create({
    baseURL: HOST_CORE_API + "api/company/" + companyId + "/",
});

interface RetryQueueItem {
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
    config: AxiosRequestConfig;
}

let isRefreshing = false;
const refreshAndRetryQueue: RetryQueueItem[] = [];

// NOT_AUTHORIZED handler / refresh token
const handleTokenRefresh = async (axiosInstance: any, originalRequest: any) => {
    isRefreshing = true;
    try {
        const token = getRefreshToken();
        const res = await axiosAuthInstance.post("auth/refresh-token", {
            refreshToken: token,
        });

        const newAccessToken = res.data.accessToken;
        updateAccessToken(newAccessToken);

        // Retry all requests in the queue with the new token
        refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
            if (config && config.headers) {
                config.headers.Authorization = `Bearer ${res.data.accessToken}`;
                axiosInstance
                    .request(config)
                    .then((response: any) => resolve(response))
                    .catch((err: any) => reject(err));
            }
        });

        // Clear the queue
        refreshAndRetryQueue.length = 0;

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${res.data.accessToken}`;
        return axiosInstance(originalRequest);
    } catch (refreshError) {
        window.location.href = PATH_LOGOUT;
    } finally {
        isRefreshing = false;
    }
};

// auth response
axiosAuthInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response.status === HttpStatusCode.MAINTENANCE) {
            if (window.location.pathname !== PATH_MAINTENANCE) {
                window.location.href = PATH_MAINTENANCE;
            }
        }
        return Promise.reject(error);
    }
);

// core request
axiosCoreInstance.interceptors.request.use(async (request: any) => {
    const token = getAccessToken();

    const noCurrentCompanyUrls = [
        "api/company",
        "api/company/verify-affiliate-code",
        "api/user/profile",
        "api/user/invitation",
        "api/user/permissions",
    ];

    const currentCompany = Storage.getItem("currentCompany");

    if (token) {
        if (token) (request.headers as any)["Authorization"] = token;
    }

    if (currentCompany?.id) {
        noCurrentCompanyUrls.push("api/user/permissions/" + currentCompany?.id);
    }

    if (currentCompany?.id && !noCurrentCompanyUrls.includes(request.url)) {
        (request.headers as any)["X-Company-Id"] = currentCompany?.id;
    }
    return request;
});

// core response
axiosCoreInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        const originalRequest: AxiosRequestConfig = error.config;
        if (error.response && error.response.status === 401) {
            if (!isRefreshing) {
                handleTokenRefresh(axiosCoreInstance, originalRequest);
            }

            // Add the original request to the queue
            return new Promise<void>((resolve, reject) => {
                refreshAndRetryQueue.push({ config: originalRequest, resolve, reject });
            });
        }

        if (error.response.status === HttpStatusCode.MAINTENANCE) {
            if (window.location.pathname !== PATH_MAINTENANCE) {
                window.location.href = PATH_MAINTENANCE;
            }
        }

        // Return a Promise rejection if the status code is not 401
        return Promise.reject(error);
    }
);

// company request
axiosCompanyInstance.interceptors.request.use(async (request) => {
    const token = getAccessToken();
    const currentCompany = Storage.getItem("currentCompany");

    request.baseURL = HOST_CORE_API + "api/company/" + currentCompany?.id + "/";

    if (token) {
        if (token) (request.headers as any)["Authorization"] = token;
    }
    if (currentCompany?.id) {
        (request.headers as any)["X-Company-Id"] = currentCompany?.id;
    }

    return request;
});

// company response
axiosCompanyInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const originalRequest: AxiosRequestConfig = error.config;
        if (error.response && error.response.status === 401) {
            if (!isRefreshing) {
                handleTokenRefresh(axiosCompanyInstance, originalRequest);
            }

            // Add the original request to the queue
            return new Promise<void>((resolve, reject) => {
                refreshAndRetryQueue.push({ config: originalRequest, resolve, reject });
            });
        }

        if (error.response.status === HttpStatusCode.MAINTENANCE) {
            if (window.location.pathname !== PATH_MAINTENANCE) {
                window.location.href = PATH_MAINTENANCE;
            }
        }
        return Promise.reject(error);
    }
);

// payment request
axiosPaymentInstance.interceptors.request.use(async (request: any) => {
    const token = getAccessToken();
    const currentCompany = Storage.getItem("currentCompany");

    if (token) {
        if (token) (request.headers as any)["Authorization"] = token;
    }
    if (currentCompany?.id) {
        (request.headers as any)["X-Company-Id"] = currentCompany?.id;
    }
    return request;
});

// payment response
axiosPaymentInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const originalRequest: AxiosRequestConfig = error.config;
        if (error.response && error.response.status === 401) {
            if (!isRefreshing) {
                handleTokenRefresh(axiosPaymentInstance, originalRequest);
            }

            // Add the original request to the queue
            return new Promise<void>((resolve, reject) => {
                refreshAndRetryQueue.push({ config: originalRequest, resolve, reject });
            });
        }
        if (error.response.status === HttpStatusCode.MAINTENANCE) {
            if (window.location.pathname !== PATH_MAINTENANCE) {
                window.location.href = PATH_MAINTENANCE;
            }
        }
        return Promise.reject(error);
    }
);

export enum HttpStatusCode {
    OK = 200,
    CREATED = 201,
    NOT_AUTHORIZED = 401,
    MAINTENANCE = 503,
}
